// ---------------------------------------------------------------------------
// WHO SEES WHAT. Pure functions over the /v1/me payload — no React, no router,
// no fetch — so every rule below is assertable under `node --test`.
//
// THIS IS CHROME, NOT SECURITY, AND THE DISTINCTION IS THE WHOLE FILE.
//
// Nothing here decides whether an action is permitted. The API decides that
// (api/src/rbac.ts, rule R7 — authorise against the resolved record) and the
// database decides it again (row-level security). What this file decides is
// which screen to draw, so that a worker who taps a console link is sent
// somewhere useful instead of an empty page full of refused requests.
//
// If every function here returned `allow`, the product would still be secure
// and would merely be rude. That is the correct amount of load for a client-
// side check to bear.
//
// RULE A1 SHOWS UP HERE TOO, IN ITS USER-INTERFACE FORM. No session resolves to
// no organisation — never to "the first one". `active_org` being null with
// several memberships is a REAL STATE with a REAL SCREEN (choose-org), not a
// gap to be filled in with a guess. V3 made the same choice in the API for the
// same reason, and a client that quietly picked one would put the guess back.
// ---------------------------------------------------------------------------

import { may, type Me } from "../api/auth";
import { SURFACE_PREFIX, type Surface } from "../system/navigation";
import { hubGrantWaiting } from "../screens/hubGrant";

/**
 * The permission a surface needs before it will show anything.
 *
 * These are ids from `app.permission`, and verify-V4.sh reads that table and
 * checks every one of them exists — R8's habit applied to the browser. A typo
 * here would hide a surface from everybody, forever, and would look exactly
 * like a permissions bug in the API.
 *
 * `org.read` is held by every internal role and by neither external one, which
 * makes it the honest test for "may this person see the agency's console" —
 * honest because it follows the data rather than a list of role names written
 * out here, which is what absolute rule 4 exists to prevent.
 */
export const SURFACE_REQUIRES: Record<Surface, string> = {
  console: "org.read",
  portal: "event.read",
  worker: "assignment.read_own",
};

/**
 * Which surface someone lands on when they qualify for more than one.
 *
 * Ordered, not "the first match in an object" — object key order is a language
 * detail and this is a product decision. Staff go to the console; somebody who
 * is only ever a worker goes to the worker app.
 */
export const SURFACE_PREFERENCE: readonly Surface[] = ["console", "worker", "portal"] as const;

/** How the /v1/me request is going, from the caller's point of view. */
export type LoadState = "loading" | "ready" | "error";

export type Gate =
  /** Still asking. Draw nothing that will move. */
  | { kind: "loading" }
  /** The API could not be reached. NOT the same as "signed out". */
  | { kind: "unavailable" }
  | { kind: "sign-in" }
  /** Signed in, several organisations, none chosen. A1 in its UI form. */
  | { kind: "choose-org" }
  /** Signed in and attached to nothing this product can show them. */
  | { kind: "no-access" }
  | { kind: "redirect"; to: string }
  | { kind: "allow" };

/**
 * V17a. Verbunk staff, and the ONE thing this file may conclude from it.
 *
 * A platform reviewer holds neither `org.read` nor any other tenant permission — by
 * design, and `0041`'s header argues it at length — so every surface gate below refuses
 * them, and correctly: they are not an agency's staff. But they still need a page to
 * land on, and "no access" is the wrong answer for somebody the API will happily serve.
 *
 * THE FLAG COMES FROM THE SERVER because the browser cannot derive it. A null
 * `active_org` is already a real state with a real screen — `choose-org` — so a client
 * seeing one cannot tell staff from somebody with two memberships who has picked
 * neither.
 */
export function isPlatformStaff(me: Me): boolean {
  return me.authenticated && me.is_platform === true;
}

export function isEligible(me: Me, surface: Surface): boolean {
  // PLATFORM STAFF ARE ELIGIBLE FOR THE CONSOLE AND NOTHING ELSE. Not because they
  // hold its permission — they do not — but because the console is the shell an
  // internal operations desk belongs in, and inventing a fifth `data-surface` for one
  // screen would mean a new contrast-tested token set that
  // `verify-design-parity.sh` asserts the completeness of. Recorded in run 31.
  //
  // AND THIS IS CHROME. The desk's own routes are refused by `requirePlatform` and
  // again inside `app.platform_review_queue()`, so admitting staff to a shell grants
  // them nothing: every tenant screen inside it will answer with a refusal, which is
  // exactly what this file says it is for.
  if (surface === "console" && isPlatformStaff(me)) return true;
  return may(me, SURFACE_REQUIRES[surface]);
}

/** The first surface in preference order this person can use, or null. */
export function homeSurfaceFor(me: Me): Surface | null {
  for (const surface of SURFACE_PREFERENCE) {
    if (isEligible(me, surface)) return surface;
  }
  return null;
}

export function homePathFor(me: Me): string | null {
  const surface = homeSurfaceFor(me);
  // V-ETA-2b. An event.clinic request link opened before signing in is waiting in this
  // tab: every sign-in path comes through here, so this is the one place that returns to it.
  if (surface === "console" && hubGrantWaiting()) return `${SURFACE_PREFIX.console}/hub-requests`;
  return surface ? SURFACE_PREFIX[surface] : null;
}

/**
 * The one decision, in one place.
 *
 * ORDER IS LOAD-BEARING and each step is here for a reason:
 *
 *   1. loading before anything — a flash of the sign-in screen on every cold
 *      load is how a signed-in user learns not to trust the page.
 *   2. `error` is NOT `sign-in`. A dropped connection that logs somebody out
 *      trains them to re-enter a password whenever the network hiccups, which
 *      is a phishing lesson taught by their own employer.
 *   3. choose-org before eligibility, because permissions are reported FOR the
 *      active organisation. With none chosen the permission list is empty and
 *      every surface would look forbidden.
 */
export function gateFor(state: LoadState, me: Me | null, surface: Surface): Gate {
  if (state === "loading") return { kind: "loading" };
  if (state === "error" || me === null) return { kind: "unavailable" };
  if (!me.authenticated) return { kind: "sign-in" };
  if (me.memberships.length === 0) return { kind: "no-access" };
  if (me.active_org === null) return { kind: "choose-org" };
  if (isEligible(me, surface)) return { kind: "allow" };

  const home = homePathFor(me);
  if (home === null) return { kind: "no-access" };
  return { kind: "redirect", to: home };
}
